/**
 * Image Processing Utilities
 * Auto-generate thumbnails from uploaded images
 */

export interface ImageVariants {
  original: File;
  thumbnail: File;
  fullUrl?: string;
  thumbUrl?: string;
}

export interface ProcessingOptions {
  thumbWidth?: number;
  thumbHeight?: number;
  quality?: number;
}

const DEFAULT_OPTIONS: Required<ProcessingOptions> = {
  thumbWidth: 480,
  thumbHeight: 480, // Square thumbnails for square posts
  quality: 0.85
};

/**
 * Generate thumbnail from image file using Canvas API
 */
export async function generateThumbnail(
  file: File,
  options: ProcessingOptions = {}
): Promise<File> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Canvas context not available'));
      return;
    }

    img.onload = () => {
      // Calculate dimensions maintaining aspect ratio
      const aspectRatio = img.width / img.height;
      const targetAspectRatio = opts.thumbWidth / opts.thumbHeight;

      let width, height;

      if (aspectRatio > targetAspectRatio) {
        // Image is wider - fit to width
        width = opts.thumbWidth;
        height = Math.round(width / aspectRatio);
      } else {
        // Image is taller - fit to height
        height = opts.thumbHeight;
        width = Math.round(height * aspectRatio);
      }

      canvas.width = width;
      canvas.height = height;

      // Draw resized image
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to blob
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to generate thumbnail blob'));
            return;
          }

          // Create File from blob with proper name
          const thumbFileName = file.name.replace(/(\.\w+)$/, '_thumb$1');
          const thumbFile = new File([blob], thumbFileName, {
            type: 'image/webp',
            lastModified: Date.now()
          });

          resolve(thumbFile);
        },
        'image/webp',
        opts.quality
      );
    };

    img.onerror = () => {
      reject(new Error(`Failed to load image: ${file.name}`));
    };

    // Load image from file
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Process image and generate all variants
 */
export async function processImage(
  file: File,
  options?: ProcessingOptions
): Promise<ImageVariants> {
  // Validate file is an image
  if (!file.type.startsWith('image/')) {
    throw new Error(`File is not an image: ${file.type}`);
  }

  // Generate thumbnail
  const thumbnail = await generateThumbnail(file, options);

  return {
    original: file,
    thumbnail
  };
}

/**
 * Process multiple images in parallel
 */
export async function processImages(
  files: File[],
  options?: ProcessingOptions
): Promise<ImageVariants[]> {
  const promises = files.map(file => processImage(file, options));
  return Promise.all(promises);
}

/**
 * Extract video thumbnail (first frame at 1 second)
 */
export async function extractVideoThumbnail(
  file: File,
  timeInSeconds: number = 1
): Promise<File> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Canvas context not available'));
      return;
    }

    video.onloadedmetadata = () => {
      // Set video to specific time
      video.currentTime = Math.min(timeInSeconds, video.duration);
    };

    video.onseeked = () => {
      // Set canvas size to video size (or resize to thumbnail size)
      const aspectRatio = video.videoWidth / video.videoHeight;
      const thumbHeight = 720;
      const thumbWidth = Math.round(thumbHeight * aspectRatio);

      canvas.width = thumbWidth;
      canvas.height = thumbHeight;

      // Draw current video frame
      ctx.drawImage(video, 0, 0, thumbWidth, thumbHeight);

      // Convert to blob
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to generate video thumbnail'));
            return;
          }

          const thumbFileName = file.name.replace(/\.\w+$/, '_thumb.webp');
          const thumbFile = new File([blob], thumbFileName, {
            type: 'image/webp',
            lastModified: Date.now()
          });

          // Cleanup
          URL.revokeObjectURL(video.src);
          resolve(thumbFile);
        },
        'image/webp',
        0.85
      );
    };

    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      reject(new Error(`Failed to load video: ${file.name}`));
    };

    // Load video from file
    video.src = URL.createObjectURL(file);
    video.load();
  });
}

/**
 * Get file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Validate image dimensions
 */
export async function validateImageDimensions(
  file: File,
  expectedWidth?: number,
  expectedHeight?: number
): Promise<{ width: number; height: number; valid: boolean }> {
  return new Promise((resolve) => {
    const img = new Image();

    img.onload = () => {
      const valid = (!expectedWidth || img.width === expectedWidth) &&
                   (!expectedHeight || img.height === expectedHeight);

      resolve({
        width: img.width,
        height: img.height,
        valid
      });

      URL.revokeObjectURL(img.src);
    };

    img.onerror = () => {
      resolve({ width: 0, height: 0, valid: false });
      URL.revokeObjectURL(img.src);
    };

    img.src = URL.createObjectURL(file);
  });
}
